// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * What Message does on a contact's card, and the line the card shows when
 * this device holds no chat with the person.
 *
 * A person becomes a contact only when this device joins the one-to-one with
 * them (2026-11-04), so neither an Accept nor an acknowledgement leaves a
 * contact whose chat is "being set up". A contact with no chat on this
 * device came from a backup or another device of the account and waits for
 * a device of that person to add this one; Message never founds a second
 * one-to-one beside the one that exists elsewhere. The same rule as iOS
 * (`ContactDetailScreen.noChatLineKey`).
 */

/** `go`: the chat is on this device. `unavailable`: the contact's account is
 *  not known, so no chat can come from here. `not-on-device`: the chat
 *  exists but not on this device yet. */
export type MessageAction = "go" | "unavailable" | "not-on-device";

export function messageAction(facts: {
  readonly hasChat: boolean;
  /** Whether the contact's account id is known. */
  readonly accountKnown: boolean;
}): MessageAction {
  if (facts.hasChat) return "go";
  return facts.accountKnown ? "not-on-device" : "unavailable";
}

/** The catalog key of the line under a contact with no chat on this device,
 *  or `null` when there is a chat. */
export function noChatLineKey(
  action: MessageAction,
): "contacts.detail.message_unavailable" | "chats.new_chat.not_available" | null {
  switch (action) {
    case "go":
      return null;
    case "unavailable":
      return "contacts.detail.message_unavailable";
    case "not-on-device":
      return "chats.new_chat.not_available";
  }
}
